/**
 * Readers for the message fields admin screens surface out of a `catch`
 * binding.
 *
 * Admin reaches the API through the axios instance in `@/api/axios`, so a
 * rejected request arrives as an `AxiosError`: `message` is the generic
 * "Request failed with status code NNN" axios sets itself, while the server's
 * own text sits somewhere under `response.data`. Screens read one, the other,
 * or both, in an order that varies per screen, so these helpers expose the
 * individual fields; getErrorMessage is the common response-then-thrown
 * precedence.
 *
 * Each reader walks its path defensively and yields `undefined` unless it
 * lands on a string, so a value thrown by something other than axios (a bare
 * object, a string, `undefined`) reads the same way it did under the `any`
 * annotations these replaced, without asserting a shape onto `unknown`. The
 * one deliberate narrowing: a non-string value at the end of a path now reads
 * as `undefined` instead of being interpolated as `[object Object]`.
 */

/**
 * Follows `path` through a caught value and returns the leaf only when it is
 * a string. Any non-object link in the chain (including `null`) stops the
 * walk, mirroring the optional chaining these call sites used to do inline.
 */
export const readErrorField = (
  error: unknown,
  ...path: string[]
): string | undefined => {
  let current = error;
  for (const key of path) {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : undefined;
};

/**
 * `Error.message`. On an axios rejection this is axios' own
 * "Request failed with status code NNN", not the server's message.
 */
export const getThrownMessage = (error: unknown): string | undefined =>
  readErrorField(error, 'message');

/**
 * The server's own text on an axios rejection. The API's error body is
 * `{code, error, details: {friendlyMessage, ...}}`: `friendlyMessage` is the
 * readable reason and `error` the internal one, so both are shown (as
 * "friendly (error)") to make the failure easy to triage. A few middlewares
 * answer `{message}` instead, which is read last.
 */
export const getResponseMessage = (error: unknown): string | undefined => {
  const friendlyMessage = readErrorField(
    error,
    'response',
    'data',
    'details',
    'friendlyMessage',
  )?.trim();
  const serverError = readErrorField(
    error,
    'response',
    'data',
    'error',
  )?.trim();
  if (friendlyMessage && serverError && friendlyMessage !== serverError) {
    return `${friendlyMessage} (${serverError})`;
  }
  return (
    friendlyMessage ||
    serverError ||
    readErrorField(error, 'response', 'data', 'message')
  );
};

/**
 * The server's machine-readable error code (`response.data.code`, e.g.
 * `'DUPLICATE_REQUEST'`) on an axios rejection.
 */
export const getResponseErrorCode = (error: unknown): string | undefined =>
  readErrorField(error, 'response', 'data', 'code');

/**
 * The caller-safe payload a throw site attached via `meta.publicData` (see
 * server's ErrorRenderer): `response.data.details.data`. Unlike the other
 * readers this is not necessarily a string, so it walks the object path
 * itself rather than going through `readErrorField`.
 */
export const getResponseErrorData = (error: unknown): unknown => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const {response} = error as Record<string, unknown>;
  if (typeof response !== 'object' || response === null) {
    return undefined;
  }
  const {data} = response as Record<string, unknown>;
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }
  const {details} = data as Record<string, unknown>;
  if (typeof details !== 'object' || details === null) {
    return undefined;
  }
  return (details as Record<string, unknown>).data;
};

/**
 * The message to show for a caught value: the server's text (see
 * getResponseMessage) when there is one, else the thrown `Error.message`, else
 * `fallback`.
 */
export const getErrorMessage = (err: unknown, fallback: string): string =>
  getResponseMessage(err) || getThrownMessage(err) || fallback;
